import React from 'react';
import styled from 'styled-components';
import { Box, Heading, Stack, Text } from '@99designs/emu';
import { colors } from '@99designs/emu/settings';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';
import { CurrencyCode } from '@99designs/i18n';
import { Price } from '@99designs/spa-common';
import { Feature, FeaturesList } from '@99designs/spa-common';
import { useForm } from '../../ObservableForm';
import CheckboxCard from './CheckboxCard';
import { ContestUpgradesChoiceFieldFragment } from './ContestUpgradesChoiceField.generated';

export type Props = ContestUpgradesChoiceFieldFragment;

const HighlightedText = styled.span`
    color: ${colors.forrestLyf};
`;

export const CONTEST_UPGRADE_DESIGNER_BLOG = 'CONTEST_UPGRADE_DESIGNER_BLOG';
export const CONTEST_UPGRADE_HIGHLIGHT_ORANGE = 'CONTEST_UPGRADE_HIGHLIGHT_ORANGE';
export const CONTEST_UPGRADE_FEATURED_LISTING = 'CONTEST_UPGRADE_FEATURED_LISTING';
export const CONTEST_UPGRADE_POWER_PACK = 'CONTEST_UPGRADE_POWER_PACK';

export function Edit({ id, label, options, contestUpgradesChoiceSubtext: subtext }: Props) {
    const form = useForm();
    const { onChange } = form.attach(id, true);

    function renderDisabled(optionValue: string): boolean {
        const currentValues = form.get(id) || [];

        if (optionValue === 'CONTEST_UPGRADE_POWER_PACK') {
            return false;
        }

        if (
            (currentValues.includes('CONTEST_UPGRADE_DESIGNER_BLOG') &&
                currentValues.includes('CONTEST_UPGRADE_HIGHLIGHT_ORANGE') &&
                currentValues.includes('CONTEST_UPGRADE_FEATURED_LISTING')) ||
            currentValues.includes('CONTEST_UPGRADE_POWER_PACK')
        ) {
            return true;
        }

        return false;
    }

    function renderContestUpgradesPrice(price: number[], currency: string) {
        if (price[1] === undefined) {
            return '';
        }

        if (price[0] === 0 && price[1] === 0) {
            return 'Recommended';
        }

        return <Price amount={price[1]} currency={currency as CurrencyCode} />;
    }

    function handleOnChecked(optionValue: string) {
        const currentValues = form.get(id) || [];
        if (currentValues.includes(optionValue)) {
            const updatedValues = currentValues.filter((v: string) => optionValue !== v);
            onChange(updatedValues);
            return;
        }

        const updatedValues = [...currentValues, optionValue];
        if (
            (updatedValues.includes('CONTEST_UPGRADE_DESIGNER_BLOG') &&
                updatedValues.includes('CONTEST_UPGRADE_HIGHLIGHT_ORANGE') &&
                updatedValues.includes('CONTEST_UPGRADE_FEATURED_LISTING')) ||
            updatedValues.includes('CONTEST_UPGRADE_POWER_PACK')
        ) {
            onChange(['CONTEST_UPGRADE_POWER_PACK']);
            return;
        }

        onChange([...currentValues, optionValue]);
    }

    function renderFormattedMessage(subtext: string, promotionalText: string): string {
        if (promotionalText) {
            return `${subtext} <Text>${promotionalText}</Text>`;
        }
        return subtext;
    }

    function renderFeatureList(optionDetails: string[]): React.ReactNode {
        if (optionDetails.length > 0) {
            const features: Feature[] = optionDetails.map((detail) => {
                return {
                    isAvailable: true,
                    title: detail,
                };
            });
            return <FeaturesList features={features} />;
        }
        return null;
    }

    return (
        <Box marginY={'4bu'}>
            <Stack space={'1.5bu'}>
                <Stack space={'0.75bu'}>
                    <Heading accessibilityLevel={4}>{label}</Heading>
                    <Text>{subtext}</Text>
                </Stack>
                <Stack space="1bu">
                    {options.map((option) => {
                        return (
                            <CheckboxCard
                                key={option.value}
                                id={option.value}
                                description={option.label}
                                subText={
                                    <FormattedReactMessage
                                        message={renderFormattedMessage(
                                            option.subtext as string,
                                            option.promotionalText as string
                                        )}
                                        replacements={{
                                            Text: <HighlightedText />,
                                        }}
                                    />
                                }
                                label={renderContestUpgradesPrice(
                                    option.price as number[],
                                    option.currency as string
                                )}
                                isChecked={(form.get(id) || []).includes(option.value)}
                                isDisabled={renderDisabled(option.value)}
                                onChecked={() => handleOnChecked(option.value)}
                                children={renderFeatureList(option.optionDetails as string[])}
                            />
                        );
                    })}
                </Stack>
            </Stack>
        </Box>
    );
}

export function View({ contestUpgradesChoiceValues: values, options }: Props) {
    return (
        <ul>
            {options
                .filter((o) => values.includes(o.value))
                .map((o) => (
                    <li key={o.value}>{o.label}</li>
                ))}
        </ul>
    );
}

export function HasValue({ contestUpgradesChoiceValues }: Props): boolean {
    return contestUpgradesChoiceValues.length !== 0;
}

export function getValue({ contestUpgradesChoiceValues }: Props) {
    return contestUpgradesChoiceValues;
}
